import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Stack,
    AspectRatio,
    Skeleton,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {useProductSearch} from '@salesforce/commerce-sdk-react'
import {useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useWishList} from '../../../hooks/use-wish-list'
import ProductTile from '../../../components/product-tile'
import {useWishlistActions} from '../../../hooks/use-wishlist-action'
import {getPDPath} from '../../../utils/custom-utils'

export const RectanglePromoWithProductTile = ({
    desktopRectanglePromoImage,
    mobileRectanglePromoImage,
    rectanglePromoImageAltTag,
    rectanglePromotileUrl,
    productTileviewType,
    viewType,
    tileUrl
}) => {
    const {data: wishlist} = useWishList()
    const [productData, setProductData] = useState([])
    const [searchParams] = useSearchParams()
    const {addItemToWishlist, removeItemFromWishlist} = useWishlistActions()
    const styles = useMultiStyleConfig('containerStyles')
    const desktopImageURL = desktopRectanglePromoImage?.url
    const mobileImageURL = mobileRectanglePromoImage?.url
    const {site, locale} = useMultiSite()
    const categoryId = rectanglePromotileUrl || tileUrl
    if (categoryId) {
        searchParams._refine.push(`cgid=${categoryId}`)
    }

    const {data: productSearchResult, isLoading} = useProductSearch({
        parameters: {
            ...searchParams,
            perPricebook: true,
            allVariationProperties: true,
            allImages: true,
            refine: searchParams._refine
        }
    })

    useEffect(() => {
        if (productSearchResult?.hits) {
            setProductData(productSearchResult.hits)
        } else {
            setProductData([])
        }
    }, [productSearchResult?.hits, categoryId])

    const productsToRender =
        productTileviewType === '2'
            ? productData.slice(0, 2)
            : productTileviewType === '4'
            ? productData.slice(0, 4)
            : productData

    const promotileUrl = getPDPath(rectanglePromotileUrl, site, locale)
    return (
        <Box className="rectangle-promo" {...styles.containerStyles}>
            <Box
                display="flex"
                flexDirection={{
                    base: 'column',
                    sm:
                        viewType === 'normalView'
                            ? 'column'
                            : viewType === 'layout-2x1'
                            ? 'row'
                            : viewType === 'layout-alternate-2x1'
                            ? 'row-reverse'
                            : 'row'
                }}
            >
                {/* Image Block */}
                <Box
                    flex={{
                        base: '0 0 auto',
                        sm: viewType === 'normalView' ? '0 0 auto' : '0 0 50%'
                    }}
                    mb={6}
                >
                    <Link to={promotileUrl}>
                        <picture>
                            <source srcSet={desktopImageURL} media="(min-width: 48em)" />
                            <source srcSet={mobileImageURL} media="(max-width: 47.99em)" />
                            <img
                                className="image-with-text-image"
                                data-testid="image-with-text-image"
                                src={desktopImageURL}
                                alt={rectanglePromoImageAltTag || 'Promo Image'}
                            />
                        </picture>
                    </Link>
                </Box>

                {/* Products Block */}
                <Box
                    display="flex"
                    flexWrap="wrap"
                    justifyContent="flex-start"
                    flex={{
                        base: '0 0 auto',
                        sm: viewType === 'normalView' ? '0 0 auto' : '0 0 50%'
                    }}
                >
                    {isLoading
                        ? [0, 1, 2, 3].map((key) => (
                              <Box
                                  key={key}
                                  className="product-tile-block"
                                  flex="0 0 auto"
                                  width={{base: '100%', md: '275px'}}
                                  mx={2}
                                  style={{scrollSnapAlign: 'start'}}
                              >
                                  <Stack>
                                      <AspectRatio ratio={1}>
                                          <Skeleton />
                                      </AspectRatio>
                                      <Skeleton height="20px" width="80%" />
                                      <Skeleton height="20px" width="50%" />
                                  </Stack>
                              </Box>
                          ))
                        : productsToRender.map((product, index) => (
                              <Box
                                  key={product.productId}
                                  className="product-tile-block"
                                  flex="0 0 auto"
                                  w={{
                                      base: '50%',
                                      sm: viewType === 'normalView' ? '25%' : '50%'
                                  }}
                              >
                                  <ProductTile
                                      product={product}
                                      enableFavourite
                                      isFavourite={wishlist?.productWishList[0]?.customerProductListItems?.some(
                                          (item) => item.productId === product.productId
                                      )}
                                      onFavouriteToggle={(isFavourite, productDetail, event) => {
                                          const action = isFavourite
                                              ? (p, event) =>
                                                    addItemToWishlist(
                                                        p,
                                                        index,
                                                        productDetail,
                                                        event
                                                    )
                                              : (p) =>
                                                    removeItemFromWishlist(p, index, productDetail)
                                          return action(product, event)
                                      }}
                                  />
                              </Box>
                          ))}
                </Box>
            </Box>
        </Box>
    )
}

RectanglePromoWithProductTile.propTypes = {
    desktopRectanglePromoImage: PropTypes.shape({
        url: PropTypes.string.isRequired,
        focalPoint: PropTypes.shape({
            x: PropTypes.number,
            y: PropTypes.number
        }),
        metaData: PropTypes.shape({
            height: PropTypes.number,
            width: PropTypes.number
        })
    }),
    mobileRectanglePromoImage: PropTypes.shape({
        url: PropTypes.string.isRequired,
        focalPoint: PropTypes.shape({
            x: PropTypes.number,
            y: PropTypes.number
        }),
        metaData: PropTypes.shape({
            height: PropTypes.number,
            width: PropTypes.number
        })
    }),
    rectanglePromoImageAltTag: PropTypes.string,
    rectanglePromotileUrl: PropTypes.string,
    imageLazyload: PropTypes.string,
    productTileviewType: PropTypes.string,
    viewType: PropTypes.string,
    tileUrl: PropTypes.string
}

export default RectanglePromoWithProductTile
